import React from 'react';
import bemify, { toModifiers } from 'bemify-js';

import Icon from 'app/common/components/Icon';
import Link from 'app/common/components/Link';
import 'app/common/components/Button.scss';

import type { KindType } from 'app/common/types';

const customTags: { [key: string]: React.ComponentType<any> } = {
  Link,
};

export interface ButtonProps {
  iconAfter?: string;
  iconBefore?: string;
  iconKind?: string;
  iconSize?: number;
  tag?: string;
  type?: string;
  className?: string;
  kind?: KindType;
  text?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  onClick?: (event: React.SyntheticEvent<HTMLButtonElement>) => void;
  [key: string]: any;
}

const Button = ({
  children,
  iconAfter,
  iconBefore,
  iconKind,
  iconSize,
  kind,
  tag = 'button',
  text,
  type = 'button',
  ...props
}: ButtonProps) => {
  const bem = bemify('button');
  // Note that `flat` is hardcoded here since we want
  // all buttons moving forward to be flat, but we still
  // have a lot of work to do to properly BEMify all buttons.
  const className = bem('flat', toModifiers(kind), props.className);
  const element = customTags[tag] || tag;
  const iconAfterNode = !iconAfter ? null : (
    <Icon
      className={bem('__icon --after')}
      kind={iconKind}
      size={iconSize}
      type={iconAfter}
    />
  );
  const iconBeforeNode = !iconBefore ? null : (
    <Icon
      className={bem('__icon --before')}
      kind={iconKind}
      size={iconSize}
      type={iconBefore}
    />
  );
  const content = !(children || text) ? null : (
    <span className={bem('__text')}>{children || text}</span>
  );
  const inner = (
    <span className={bem('__inner')}>
      {iconBeforeNode}
      {content}
      {iconAfterNode}
    </span>
  );
  return React.createElement(
    element,
    {
      ...props,
      className,
      type,
    },
    inner
  );
};

// Use React.memo for performance (equivalent to pure from recompose)
export default React.memo(Button);
